/**
 * 意见反馈页面
 * 用户可以提交问题、建议和反馈
 * 黑白主题风格（Vercel 风格）
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  MessageSquare,
  Loader2,
  Upload,
  X,
  CheckCircle2,
  Bug,
  Lightbulb,
  HelpCircle,
  Star,
} from "lucide-react";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import { userApiClient, ApiException } from "@/lib/userApiClient";
import { useUserStore } from "@/stores/userStore";

type FeedbackCategory = "bug" | "feature" | "question" | "praise" | "other";

const CATEGORY_ICONS = {
  bug: Bug,
  feature: Lightbulb,
  question: HelpCircle,
  praise: Star,
  other: MessageSquare,
} as const;

/**
 * 意见反馈页面
 */
export default function Feedback() {
  const { t } = useTranslation("common");
  const { currentUser } = useUserStore();
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // 当当前用户更新时自动填充邮箱
  useEffect(() => {
    if (currentUser?.email && !email) {
      setEmail(currentUser.email);
    }
  }, [currentUser, email]);

  // 处理文件选择
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("feedback.toast.fileTooLarge"));
        return;
      }
      setAttachment(file);
    }
  };

  // 移除附件
  const removeAttachment = () => {
    setAttachment(null);
    const fileInput = document.getElementById("attachment") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // 提交反馈
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      toast.error(t("feedback.toast.descriptionRequired"));
      return;
    }

    if (!email.trim()) {
      toast.error(t("feedback.toast.emailRequired"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("category", category);
      formData.append("description", description);
      formData.append("email", email);
      if (attachment) {
        formData.append("attachment", attachment);
      }

      await userApiClient.post("/feedbacks", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setIsSuccess(true);
      toast.success(t("feedback.toast.submitSuccess"));

      setTimeout(() => {
        setIsSuccess(false);
        setDescription("");
        setAttachment(null);
      }, 3000);
    } catch (error) {
      console.error("提交反馈失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message || t("feedback.toast.submitFailed"));
      } else {
        toast.error(t("feedback.toast.submitFailed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 反馈类别列表
  const categories: FeedbackCategory[] = [
    "bug",
    "feature",
    "question",
    "praise",
    "other",
  ];

  // 获取占位符文本
  const getPlaceholder = () => {
    if (category === "bug")
      return t("feedback.form.descriptionPlaceholder.bug");
    if (category === "feature")
      return t("feedback.form.descriptionPlaceholder.feature");
    return t("feedback.form.descriptionPlaceholder.default");
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4 sm:py-6">
      {/* 页面标题 */}
      <SubsectionHeader
        title={t("feedback.title")}
        icon="MessageSquare"
        description={t("feedback.description")}
        className="mb-8"
      />

      {/* 成功提示 */}
      {isSuccess ? (
        <Card>
          <CardContent className="py-8 sm:py-12">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 sm:h-16 sm:w-16 text-foreground mx-auto mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                {t("feedback.success.title")}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                {t("feedback.success.description")}
              </p>
              <Button
                onClick={() => setIsSuccess(false)}
                variant="outline"
                className="w-full sm:w-auto"
              >
                {t("feedback.success.continue")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* 反馈类别卡片 */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4 sm:mb-6">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat];
              const isSelected = category === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`
                    p-3 sm:p-4 rounded-lg border-2 transition-all
                    ${
                      isSelected
                        ? "border-foreground bg-muted text-foreground"
                        : "border-border bg-background text-muted-foreground hover:border-foreground/50"
                    }
                  `}
                >
                  <Icon
                    className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1.5 sm:mb-2 ${isSelected ? "text-foreground" : "text-muted-foreground"}`}
                  />
                  <div className="text-xs sm:text-sm font-medium">
                    {t(`feedback.categories.${cat}.label`)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* 反馈表单 */}
          <Card>
            <CardContent className="p-4 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* 分类说明 */}
                <div className="bg-muted/50 border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    {React.createElement(CATEGORY_ICONS[category], {
                      className: "h-5 w-5 text-foreground mt-0.5",
                    })}
                    <div>
                      <h4 className="font-semibold text-foreground">
                        {t(`feedback.categories.${category}.label`)}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t(`feedback.categories.${category}.description`)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 详细描述 */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-base">
                    {t("feedback.form.description")}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={getPlaceholder()}
                    rows={6}
                    className="resize-none min-h-[120px] sm:min-h-[160px]"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    {t("feedback.form.charCount", {
                      count: description.length,
                    })}
                  </p>
                </div>

                {/* 联系邮箱 */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base">
                    {t("feedback.form.email")}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("feedback.form.emailPlaceholder")}
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    {t("feedback.form.emailHint")}
                  </p>
                </div>

                {/* 附件上传 */}
                <div className="space-y-2">
                  <Label htmlFor="attachment" className="text-base">
                    {t("feedback.form.attachment")}
                  </Label>
                  {attachment ? (
                    <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {attachment.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(attachment.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeAttachment}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        id="attachment"
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        className="hidden"
                      />
                      <Label
                        htmlFor="attachment"
                        className="flex flex-col sm:flex-row items-center justify-center gap-2 p-4 sm:p-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-foreground/50 transition-colors text-center"
                      >
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-xs sm:text-sm text-muted-foreground">
                          {t("feedback.form.uploadHint")}
                        </span>
                      </Label>
                    </div>
                  )}
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("feedback.form.supportedFormats")}
                  </p>
                </div>

                {/* 提交按钮 */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("feedback.form.submitting")}
                      </>
                    ) : (
                      <>
                        <MessageSquare className="mr-2 h-4 w-4" />
                        {t("feedback.form.submit")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
